// axiosConfig.tsx
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { transformedResponse } from 'util/transformedResponse';
import AuthService from 'services/authService';
import { installAnonInterceptors } from 'util/anonInterceptors';
import { CSRF_HEADER_NAME, clearCsrfToken, getCsrfToken } from "util/csrf";
import { apiRoutes, normalizeApiRoutePath } from "util/apiRoutes";

// Create an axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  transformResponse: [
    ...axios.defaults.transformResponse as any,
    (data: any) => transformedResponse(data as AxiosResponse),
  ],
});

// Request interceptor: attach the latest bearer token and CSRF token when available.
api.interceptors.request.use(
  async (config) => {
    if (typeof config.url === "string") {
      config.url = normalizeApiRoutePath(config.url) || config.url;
    }

    const method = String(config.method || "get").toUpperCase();
    const isMutation = ["DELETE", "PATCH", "POST", "PUT"].includes(method);
    if (isMutation && !getCsrfToken()) {
      await AuthService.ensureCsrfToken();
    }

    const token = AuthService.getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    const csrfToken = getCsrfToken();
    if (isMutation && csrfToken) {
      config.headers = config.headers || {};
      config.headers[CSRF_HEADER_NAME] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, try to refresh and retry.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const csrfErrorMessage = String(error.response?.data?.message || "");
    if (
      error.response?.status === 403
      && csrfErrorMessage.toLowerCase().includes("csrf")
      && !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;
      // Force a fresh token: the rejected token must be discarded, not reused.
      // ensureCsrfToken() without force would early-return on the still-present
      // stale cookie (cross-env or rotated secret) and the retry would resend
      // the same bad token, which is the bug this guards against.
      clearCsrfToken();
      await AuthService.ensureCsrfToken(true);
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers[CSRF_HEADER_NAME] = csrfToken;
      }
      return api(originalRequest);
    }

    const isAuthEndpoint = [
      apiRoutes.auth.refresh,
      apiRoutes.auth.signin,
      apiRoutes.auth.register,
      apiRoutes.auth.googleCallback,
      apiRoutes.auth.facebookCallback,
    ].some((path) => originalRequest.url?.includes(path));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        await AuthService.refresh(true);
        const newToken = AuthService.getAccessToken();
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        AuthService.clearAccessToken();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Install anonymous token interceptors
installAnonInterceptors(api);

export default api;
